/**
 * Forward inbound UTM parameters onto outbound links to cloud.pages.trimble.com.
 *
 * MXP-2873: visitors land on Contentful-powered sites carrying UTMs, then
 * click through to cloud.pages.trimble.com landing pages and lose campaign
 * attribution. This module:
 *   1. Reads `utm_*` from the current page URL when present
 *   2. Persists them in sessionStorage so they survive in-site navigation
 *      (same pattern TrialWorkFlow already reads for form attribution)
 *   3. Merges the stored/inbound set onto links whose hostname is exactly
 *      `cloud.pages.trimble.com`
 *
 * Click IDs (gclid, gbraid, wbraid, fbclid, msclkid, ...) are excluded by
 * construction: only keys that start with `utm_` are ever read or stored.
 */

export const UTM_FORWARD_HOSTNAME = "cloud.pages.trimble.com"

/** Ignore any utm_* value longer than this; likely not a real campaign value. */
export const MAX_UTM_VALUE_LENGTH = 200

/**
 * Individual `utm_*` keys in sessionStorage (matches TrialWorkFlow / existing
 * MXP campaign attribution storage). Not a JSON blob.
 */
export const UTM_SESSION_KEY_PREFIX = "utm_"

const UTM_PREFIX = "utm_"

/**
 * Parse `search` (e.g. `window.location.search`) and return every `utm_*`
 * param present, excluding values over `MAX_UTM_VALUE_LENGTH` chars.
 * Returns an empty object when there are no inbound UTMs.
 */
export function getInboundUtmParams(search: string): Record<string, string> {
  const params: Record<string, string> = {}
  if (!search) return params

  const searchParams = new URLSearchParams(search)
  for (const [key, value] of searchParams.entries()) {
    if (!key.startsWith(UTM_PREFIX)) continue
    if (value.length > MAX_UTM_VALUE_LENGTH) continue
    params[key] = value
  }
  return params
}

/**
 * Read every `utm_*` key currently in sessionStorage (length-capped).
 * Safe to call when sessionStorage is unavailable (returns {}).
 */
export function getStoredUtmParams(): Record<string, string> {
  const params: Record<string, string> = {}
  if (typeof sessionStorage === "undefined") return params

  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (!key?.startsWith(UTM_SESSION_KEY_PREFIX)) continue
      const value = sessionStorage.getItem(key)
      if (value == null || value.length > MAX_UTM_VALUE_LENGTH) continue
      params[key] = value
    }
  } catch {
    // Private mode / blocked storage — treat as empty.
  }
  return params
}

/**
 * When the URL carries any `utm_*` params, replace the stored campaign set
 * with those values (last-touch). Clears other previously stored `utm_*`
 * keys so an old campaign doesn't mix with a new one.
 *
 * No-ops when `inbound` is empty (keeps whatever was already stored).
 */
export function persistInboundUtmParams(inbound: Record<string, string>): void {
  if (typeof sessionStorage === "undefined") return
  if (Object.keys(inbound).length === 0) return

  try {
    const toRemove: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key?.startsWith(UTM_SESSION_KEY_PREFIX)) toRemove.push(key)
    }
    for (const key of toRemove) sessionStorage.removeItem(key)

    for (const [key, value] of Object.entries(inbound)) {
      sessionStorage.setItem(key, value)
    }
  } catch {
    // Private mode / blocked storage — skip persist; URL-only still works.
  }
}

/**
 * Resolve the UTM set to forward onto cloud.pages links:
 * - If the current URL has `utm_*`, persist them and return that set.
 * - Otherwise return whatever is already in sessionStorage.
 */
export function resolveUtmParamsForForwarding(
  search: string,
): Record<string, string> {
  const inbound = getInboundUtmParams(search)
  if (Object.keys(inbound).length > 0) {
    persistInboundUtmParams(inbound)
    return inbound
  }
  return getStoredUtmParams()
}

/**
 * Return `href` with `utmParams` merged in, if and only if `href` resolves to
 * `UTM_FORWARD_HOSTNAME`. Authored `utm_*` values are replaced; every other
 * part of the URL (other query params, path, hash) is preserved untouched.
 *
 * Returns `null` when the link should be left exactly as authored: non-
 * matching hostname, unparsable href, or no inbound UTMs to forward.
 */
export function withForwardedUtmParams(
  href: string,
  utmParams: Record<string, string>,
): string | null {
  if (!href || Object.keys(utmParams).length === 0) return null

  let url: URL
  try {
    url = new URL(href, typeof window !== "undefined" ? window.location.href : undefined)
  } catch {
    return null
  }

  if (url.hostname !== UTM_FORWARD_HOSTNAME) return null

  for (const [key, value] of Object.entries(utmParams)) {
    url.searchParams.set(key, value)
  }

  return url.toString()
}
